import { ChatwootResource } from '../../api';
import { CHATWOOT_WIDGET_KEY } from '../../config';
import { Account, Store } from '../../models';

declare global {
    interface Window {
        chatwootSDK: {
            run: (...args: unknown[]) => void;
        };
        $chatwoot: {
            setUser: (...args: unknown[]) => void;
            toggle: (...args: unknown[]) => void;
            websiteToken: string;
        };
    }
}

const configureChatwoot = (openOnLoad: boolean, zindex: number) => {
    if (!window.$chatwoot) return;

    if (openOnLoad) {
        window.$chatwoot.toggle('open');
    }
    const widgetIcon = document.getElementById(
        'woot-widget-bubble-icon'
    )?.parentElement;
    if (widgetIcon) {
        const currentStyle = widgetIcon.getAttribute('style');
        widgetIcon.setAttribute(
            'style',
            `${currentStyle}z-index: ${zindex} !important;`
        );
    }
};

const loadAnonymousChatwoot = (openOnLoad: boolean, zindex: number) => {
    window.chatwootSDK.run({
        websiteToken: CHATWOOT_WIDGET_KEY,
        baseUrl: 'https://support.alude.com.br',
        baseDomain: '.alude.com.br',
    });
    window.addEventListener('chatwoot:ready', function () {
        configureChatwoot(openOnLoad, zindex);
    });
};

const clearCookie = () => {
    if (!window.$chatwoot) return;

    /**
     * Remove the user cookie that is stored without domain
     */
    const parts = {
        [`cw_user_${window.$chatwoot.websiteToken}`]: '',
        expires: new Date('1970-01-01Z00:00:00:000').toUTCString(),
        domain: '',
        path: '/',
    };
    document.cookie = Object.entries(parts)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ');
};

export const setUser = ({
    id,
    email,
    name,
    phone,
}: {
    id: number;
    email: string | null;
    name: string;
    phone: string | null;
}) => {
    if (!window.$chatwoot) return;
    clearCookie();

    ChatwootResource.getIdentityHash().then(identity => {
        window.$chatwoot.setUser(id.toString(), {
            email: email,
            name: name,
            // eslint-disable-next-line camelcase
            phone_number: `+${phone}`,
            // eslint-disable-next-line camelcase
            identifier_hash: identity.hmac,
        });
    });
};

const loadLoggedChatwoot = (
    account: Account,
    openOnLoad: boolean,
    zindex: number
) => {
    window.chatwootSDK.run({
        websiteToken: CHATWOOT_WIDGET_KEY,
        baseUrl: 'https://support.alude.com.br',
        baseDomain: '.alude.com.br',
    });
    window.addEventListener('chatwoot:ready', function () {
        setUser({
            id: account.id,
            name: account.name,
            email: account.email,
            phone: account.phone,
        });
        configureChatwoot(openOnLoad, zindex);
    });
};

const activateChatwoot = (
    store: Store | null,
    openOnLoad: boolean,
    zindex: number
) => {
    if (store) {
        store.account.asyncAccount.then(account => {
            if (account) {
                loadLoggedChatwoot(account, openOnLoad, zindex);
            } else {
                loadAnonymousChatwoot(openOnLoad, zindex);
            }
        });
    } else {
        loadAnonymousChatwoot(openOnLoad, zindex);
    }

    window.aludeCookies.setCookie('chatwootSession', new Date().getTime());
};

export const loadChatwoot = (
    store: Store | null,
    openOnLoad = false,
    zindex = 1
): void => {
    /**
     * Chatwoot snippet
     */
    (function (e, t) {
        const s = e.createElement(t) as HTMLScriptElement;
        const r = e.getElementsByTagName(t)[0];
        s.src = 'https://support.alude.com.br/packs/js/sdk.js';
        s.defer = !0;
        s.async = !0;
        r.parentNode?.insertBefore(s, r);
        s.addEventListener('load', () => {
            activateChatwoot(store, openOnLoad, zindex);
        });
    })(document, 'script');
};
